import { useState } from "react";
import { Copy, ExternalLink, FileText, Plus, Trash2 } from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSignedUrl } from "../calendar/useSignedUrl";
import type { BrandAsset, BrandAssetKind } from "../types";
import {
  CLIENT_FILES_BUCKET,
  MAX_CLIENT_FILE_BYTES,
  isImagePath,
  removeClientFile,
  uploadClientFile,
} from "./clientFiles";

const KIND_ORDER: BrandAssetKind[] = [
  "logo",
  "color",
  "font",
  "manual",
  "brief",
  "photo",
  "other",
];

const HEX_COLOR = /^#?[0-9a-f]{3}([0-9a-f]{3})?$/i;

export const BrandKit = ({
  companyId,
  canManage,
}: {
  companyId: Identifier;
  canManage: boolean;
}) => {
  const translate = useTranslate();
  const [isAdding, setIsAdding] = useState(false);
  const { data: assets = [], isPending } = useGetList<BrandAsset>(
    "brand_assets",
    {
      filter: { company_id: companyId },
      sort: { field: "created_at", order: "ASC" },
      pagination: { page: 1, perPage: 300 },
    },
  );

  const groups = KIND_ORDER.map((kind) => ({
    kind,
    items: assets.filter((asset) => asset.kind === kind),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex flex-col gap-5">
      {canManage ? (
        isAdding ? (
          <AssetForm companyId={companyId} onDone={() => setIsAdding(false)} />
        ) : (
          <Button
            variant="outline"
            className="self-start rounded-full"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="size-4" />
            {translate("resources.brand_assets.add")}
          </Button>
        )
      ) : null}

      {!isPending && assets.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.brand_assets.empty")}
        </p>
      ) : null}

      {groups.map(({ kind, items }) => (
        <div key={kind} className="flex flex-col gap-2.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {translate(`resources.brand_assets.kinds.${kind}`)}
          </h3>
          <div
            className={
              kind === "color"
                ? "flex flex-wrap gap-3"
                : kind === "logo" || kind === "photo"
                  ? "grid gap-3 grid-cols-[repeat(auto-fill,minmax(160px,1fr))]"
                  : "flex flex-col gap-2"
            }
          >
            {items.map((asset) =>
              kind === "color" ? (
                <ColorSwatch
                  key={asset.id}
                  asset={asset}
                  canManage={canManage}
                />
              ) : kind === "logo" || kind === "photo" ? (
                <ImageTile key={asset.id} asset={asset} canManage={canManage} />
              ) : (
                <AssetRow key={asset.id} asset={asset} canManage={canManage} />
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const useRemoveAsset = (asset: BrandAsset) => {
  const translate = useTranslate();
  const [deleteOne] = useDelete<BrandAsset>();
  const refresh = useRefresh();
  return async () => {
    if (!window.confirm(translate("resources.brand_assets.delete_confirm"))) {
      return;
    }
    if (asset.file_path) await removeClientFile(asset.file_path);
    deleteOne(
      "brand_assets",
      { id: asset.id, previousData: asset },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );
  };
};

const DeleteButton = ({ asset }: { asset: BrandAsset }) => {
  const remove = useRemoveAsset(asset);
  return (
    <button
      type="button"
      onClick={remove}
      aria-label="delete"
      className="text-muted-foreground hover:text-destructive"
    >
      <Trash2 className="size-3.5" />
    </button>
  );
};

const ColorSwatch = ({
  asset,
  canManage,
}: {
  asset: BrandAsset;
  canManage: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const hex = asset.value?.startsWith("#")
    ? asset.value
    : `#${asset.value ?? ""}`;
  return (
    <div className="w-36 overflow-hidden rounded-2xl bg-secondary">
      <div className="h-20" style={{ background: hex }} />
      <div className="flex items-center justify-between gap-1 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{asset.label}</div>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(hex.toUpperCase());
              notify("resources.brand_assets.copied", { type: "success" });
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {hex.toUpperCase()}
            <Copy className="size-3" />
          </button>
        </div>
        {canManage ? <DeleteButton asset={asset} /> : null}
      </div>
      <span className="sr-only">
        {translate("resources.brand_assets.kinds.color")}
      </span>
    </div>
  );
};

const ImageTile = ({
  asset,
  canManage,
}: {
  asset: BrandAsset;
  canManage: boolean;
}) => {
  const signedUrl = useSignedUrl(asset.file_path, CLIENT_FILES_BUCKET);
  const href = asset.external_url ?? signedUrl ?? undefined;
  const showImage =
    asset.file_path && isImagePath(asset.file_path) && signedUrl;
  return (
    <div className="overflow-hidden rounded-2xl bg-secondary">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="grid h-28 place-items-center bg-[repeating-conic-gradient(#1f1f25_0%_25%,#17171b_0%_50%)] bg-[length:16px_16px] p-3"
      >
        {showImage ? (
          <img
            src={signedUrl}
            alt={asset.label}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <ExternalLink className="size-5 text-muted-foreground" />
        )}
      </a>
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span className="truncate text-sm">{asset.label}</span>
        {canManage ? <DeleteButton asset={asset} /> : null}
      </div>
    </div>
  );
};

const AssetRow = ({
  asset,
  canManage,
}: {
  asset: BrandAsset;
  canManage: boolean;
}) => {
  const translate = useTranslate();
  const signedUrl = useSignedUrl(asset.file_path, CLIENT_FILES_BUCKET);
  const href = asset.external_url ?? signedUrl ?? undefined;
  return (
    <div className="flex items-center gap-3 rounded-xl bg-secondary px-3 py-2.5 text-sm">
      <FileText className="size-4 shrink-0 text-primary" />
      <span className="min-w-0 flex-1">
        <span className="font-medium">{asset.label}</span>
        {asset.value ? (
          <span className="block truncate text-xs text-muted-foreground">
            {asset.value}
          </span>
        ) : null}
      </span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-primary"
        >
          {translate("resources.brand_assets.open")}
        </a>
      ) : null}
      {canManage ? <DeleteButton asset={asset} /> : null}
    </div>
  );
};

const AssetForm = ({
  companyId,
  onDone,
}: {
  companyId: Identifier;
  onDone: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate<BrandAsset>();
  const [kind, setKind] = useState<BrandAssetKind>("logo");
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [link, setLink] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const needsFileOrLink = kind !== "color" && kind !== "font";

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!label.trim()) {
      setError(translate("ra.validation.required"));
      return;
    }
    if (kind === "color" && !HEX_COLOR.test(value.trim())) {
      setError(translate("resources.brand_assets.value_color"));
      return;
    }
    if (needsFileOrLink && !file && !link.trim()) {
      setError(translate("resources.client_reports.need_file_or_link"));
      return;
    }
    if (file && file.size > MAX_CLIENT_FILE_BYTES) {
      setError(translate("resources.content_pieces.versions.too_large"));
      return;
    }
    setIsSaving(true);
    try {
      const filePath = file
        ? await uploadClientFile(companyId, "brand", file)
        : null;
      const normalizedValue =
        kind === "color"
          ? `#${value.trim().replace(/^#/, "")}`
          : value.trim() || null;
      create(
        "brand_assets",
        {
          data: {
            company_id: companyId,
            kind,
            label: label.trim(),
            value: normalizedValue,
            file_path: filePath,
            external_url: link.trim() || null,
          },
        },
        {
          onSuccess: () => {
            refresh();
            notify("resources.brand_assets.saved", { type: "success" });
            onDone();
          },
          onError: () =>
            notify("ra.notification.http_error", { type: "error" }),
          onSettled: () => setIsSaving(false),
        },
      );
    } catch {
      setIsSaving(false);
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-2xl border border-border p-4"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>{translate("resources.brand_assets.kind")}</Label>
          <Select
            value={kind}
            onValueChange={(next) => {
              setKind(next as BrandAssetKind);
              setError(null);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KIND_ORDER.map((option) => (
                <SelectItem key={option} value={option}>
                  {translate(`resources.brand_assets.kinds.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="asset-label">
            {translate("resources.brand_assets.label")}
          </Label>
          <Input
            id="asset-label"
            value={label}
            onChange={(event) => {
              setLabel(event.target.value);
              setError(null);
            }}
          />
        </div>
      </div>
      {kind !== "logo" && kind !== "photo" ? (
        <Input
          placeholder={translate(
            kind === "color"
              ? "resources.brand_assets.value_color"
              : kind === "font"
                ? "resources.brand_assets.value_font"
                : "resources.brand_assets.value_other",
          )}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
        />
      ) : null}
      {kind !== "color" ? (
        <>
          <label className="text-xs text-muted-foreground">
            {translate("resources.brand_assets.file")}
            <Input
              type="file"
              accept="image/*,application/pdf"
              className="mt-1"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setError(null);
              }}
            />
          </label>
          <Input
            placeholder={translate("resources.brand_assets.link")}
            value={link}
            onChange={(event) => {
              setLink(event.target.value);
              setError(null);
            }}
          />
        </>
      ) : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          {translate("ra.action.cancel")}
        </Button>
        <Button type="submit" className="rounded-full" disabled={isSaving}>
          {translate("ra.action.save")}
        </Button>
      </div>
    </form>
  );
};
